import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-date-field>` says about itself.
 *
 * It is `CaosBaseField` with the inner input's type set to `date`, and that one word is the whole
 * component: the calendar popup, the segmented keyboard entry, the locale-appropriate display order
 * and the up/down stepping on each segment all come from the platform's own date control. Nothing
 * here draws a calendar, so nothing here can disagree with the one the person already knows.
 *
 * THE VALUE IS ISO, THE DISPLAY IS NOT. `value` is always `YYYY-MM-DD` — that is what a native date
 * input reads and writes — while what appears in the box follows the browser's locale. A page
 * reading the value back gets the ISO form regardless of what was on screen, which is the property
 * worth relying on and the one that surprises people.
 *
 * THREE TEMPORAL PRESENTATIONS, ONE FIELD. `format` chooses which control is drawn: `date` for a
 * day, `time` for a time of day, `datetime` for both. CAOS-600 added the latter two, so this is the
 * control FieldRenderer uses for a `Time` and for a `DateTime` as well as a `Date` — a `DateTime`
 * edits as a day and a time, not by its date part alone, which used to write midnight over whatever
 * time the value held. `ZonedDateTime` is the one temporal kind this control still cannot hold: a
 * wall-clock time in a named zone is two stored parts, and there is no control here for the zone.
 */
const docs: ComponentDocs = {
  aliases: ['date picker', 'calendar field', 'date input', 'due date field', 'time input', 'time picker', 'time field', 'datetime input', 'datetime picker'],

  answers: [
    'ask for a calendar date',
    'let somebody pick a day from a calendar',
    'bound a date to a window that is allowed',
    'record a due date or a start date on a form',
  ],

  placements: ['recordPage', 'appPage', 'screen'],

  inputs: [
    {
      name: 'label',
      attribute: 'label',
      type: 'text',
      description:
        "The field's name, drawn above the control and pointed at the input, so clicking it focuses the " +
        'control. Left unset, no label is drawn.',
    },
    {
      name: 'value',
      attribute: 'value',
      type: 'date',
      description:
        'The date, always as `YYYY-MM-DD`, whatever order the browser displays it in. Written back to this ' +
        'attribute as the person edits.',
    },
    {
      name: 'min',
      attribute: 'min',
      type: 'date',
      description: 'The earliest selectable date, as `YYYY-MM-DD`. Days before it are greyed in the calendar.',
    },
    {
      name: 'max',
      attribute: 'max',
      type: 'date',
      description: 'The latest selectable date, as `YYYY-MM-DD`. Days after it are greyed in the calendar.',
    },
    {
      name: 'step',
      attribute: 'step',
      type: 'number',
      description: 'The stepping interval in DAYS — `7` to allow only one day of the week within the window.',
    },
    {
      name: 'placeholder',
      attribute: 'placeholder',
      type: 'text',
      description:
        'Forwarded to the inner input. A native date control draws its own empty format hint and mostly ' +
        'ignores this; it is honoured here because the shared implementation forwards it, not because a date ' +
        'input has a use for it.',
    },
    {
      name: 'name',
      attribute: 'name',
      type: 'text',
      description: 'The form name forwarded to the inner input, for a field submitted as part of a form.',
    },
    {
      name: 'required',
      attribute: 'required',
      type: 'boolean',
      description: 'This field has to be filled in. Draws the asterisk beside the label and marks the input required.',
      default: false,
    },
    {
      name: 'disabled',
      attribute: 'disabled',
      type: 'boolean',
      description: 'You may not change this date. Greys the control and refuses focus.',
      default: false,
    },
    {
      name: 'invalid',
      attribute: 'invalid',
      type: 'boolean',
      description: 'The date is wrong. Turns the border and the message red and marks the input invalid for a screen reader.',
      default: false,
    },
    {
      name: 'message',
      attribute: 'message',
      type: 'text',
      description:
        'The line under the control — muted helper text on its own, the red reason when `invalid` is also set. ' +
        'A greyed-out day in the calendar says a date is not allowed; only this says why.',
    },
    {
      name: 'format',
      attribute: 'format',
      type: 'text',
      // A CLOSED set, declared as such: these are the three temporal affordances the platform has,
      // and a page editor should offer them rather than a free text box somebody can misspell.
      options: ['date', 'time', 'datetime'],
      description:
        'Which temporal control is drawn: `date` (the default) for a day, `time` for a time of day, ' +
        '`datetime` for both together. One field, three presentations — the same value attribute, label, ' +
        'validity and message in each. The field renderer picks it from the kind being edited: a `Time` ' +
        'field is a time, a `DateTime` is a date and a time, a `Date` is a date. Anything unrecognised ' +
        'reads as `date`, so a format nothing can draw degrades to a calendar rather than to a control ' +
        'with no affordance at all.',
      default: 'date',
    },
    {
      name: 'inline',
      attribute: 'inline',
      type: 'boolean',
      description:
        'Draw the record-detail variant instead of the boxed form control: no box, an accent underline, ' +
        'inheriting the surrounding type. The label and the message are hidden.',
      default: false,
    },
  ],

  // Plain DOM events, re-dispatched from the shadow root with `composed` so they cross the boundary.
  events: [
    { name: 'input', when: 'As the date is edited, after the value attribute has been written back.', carries: 'nothing' },
    {
      name: 'change',
      when:
        'When the value becomes COMPLETE, which is not the same as the person being finished. Picked from ' +
        'the calendar, or typed — and on a field that already held a value, as soon as ONE segment is ' +
        'changed, because the rest were already filled in. Do not read this as "and left": it fires while ' +
        'the control still has focus. Whoever needs "finished" should listen for the focus leaving instead.',
      carries: 'nothing',
    },
  ],

  // Nothing to read back that cannot also be set. `value` can be read off the element, but a page can
  // set it too, so it is documented as an input rather than here.
  state: [],

  slots: [],

  tokens: [
    { token: '--caos-font', controls: 'The typeface of the label, the control and the message.' },
    { token: '--caos-text-body', controls: 'The size of the label and of the date.' },
    { token: '--caos-space-1', controls: 'The gap between the label, the control and the message.' },
    { token: '--caos-space-3', controls: 'The vertical padding inside the control.' },
    { token: '--caos-radius', controls: 'The corner radius of the control.' },
    { token: '--caos-color-border', controls: 'The outline at rest.' },
    { token: '--caos-color-border-strong', controls: 'The outline while the control has focus.' },
    { token: '--caos-color-surface-2', controls: 'The fill of the control.' },
    { token: '--caos-color-text', controls: 'The colour of the date.' },
    { token: '--caos-color-text-muted', controls: 'The label, the empty format hint, the message and disabled text.' },
    { token: '--caos-color-accent', controls: 'The hover outline, and the underline of the inline variant.' },
    { token: '--caos-accent-wash', controls: 'The tint that fills the control while it has focus, in place of a focus ring.' },
    { token: '--caos-color-danger', controls: 'The required asterisk, and the outline and message when invalid.' },
  ],

  parts: [
    { part: 'label', is: 'the label above the control' },
    { part: 'input', is: 'the inner date input' },
    { part: 'message', is: 'the line under the control' },
  ],

  methods: [
    {
      name: 'focus',
      description:
        'Focus the control, on its first date segment. The shared implementation also tries to select the ' +
        'text; a date input does not support selection, and the attempt is swallowed rather than thrown.',
      args: [
        {
          name: 'options',
          type: 'FocusOptions',
          description: 'The ordinary focus options — pass `{ preventScroll: true }` to focus without scrolling to it.',
        },
      ],
    },
  ],

  placing:
    'Placed as an item in a page section like any other field. The one thing to leave room for is the ' +
    'calendar: the browser draws it in its own top layer, so it will not be clipped by whatever holds the ' +
    'field, but it does open downward over what is beneath it.',

  whenToUse:
    'A date, a time of day, or both, where the person is better served by a picker than by typing. Pick ' +
    'the presentation with `format`. The one temporal value it cannot hold is a wall-clock time in a named ' +
    'zone — see the note below.',

  insteadUse: [
    {
      name: 'FieldRenderer',
      when: 'The type is not known until the record is read — hand it the field metadata and it draws the right control.',
    },
    {
      name: 'TextField',
      when: 'The value is a period rather than a day — "Q3 2026", "week 14" — which a calendar cannot express.',
    },
    { name: 'NumberField', when: 'It is a duration or an age rather than a point on the calendar.' },
    { name: 'PicklistField', when: 'Only a handful of specific dates are on offer, and the calendar is more choice than the person has.' },
  ],

  accessibility:
    'The control is a real date input with a generated id and a label pointing at it, so it is announced with ' +
    'its name, clicking the label focuses it, and everything the platform gives a date input comes with it: ' +
    'segment-by-segment keyboard entry, up/down stepping, and the calendar opened from the keyboard. ' +
    '`required` and `invalid` are forwarded as `required` and `aria-invalid`. What is left to the author is ' +
    'the label, and the message — a person using the keyboard cannot see that a day is greyed out.',

  examples: [
    {
      key: 'due-date',
      label: 'A date on a form',
      summary: 'The plain case: a labelled calendar date already filled in.',
      placement: {
        component: 'caos-date-field',
        inputs: { label: 'Due date', value: '2026-09-15' },
      },
    },
    {
      key: 'window',
      label: 'Bounded to a window',
      summary:
        'The same field with only one quarter selectable — the calendar greys everything outside the bounds, ' +
        'and the message says in words what the greying means.',
      placement: {
        component: 'caos-date-field',
        inputs: {
          label: 'Due date',
          value: '2026-09-15',
          min: '2026-07-01',
          max: '2026-09-30',
          message: 'Dates in the current quarter only.',
        },
      },
    },
  ],
};

export default docs;
